declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface GoogleAdsConfig {
  googleAdsId: string;
  conversionLabel?: string;
}

interface PurchaseProps {
  orderId?: string;
  totalValue?: number;
  currency?: string;
}

interface ViewItemProps {
  itemId?: string;
  itemName?: string;
  value?: number;
  currency?: string;
}

interface GoogleAdsService {
  trackPageView: () => void;

  viewItem: (props: ViewItemProps) => void;

  addToCart: (
    productId?: string,
    productName?: string,
    productPrice?: number,
    productQuantity?: number,
  ) => void;

  beginCheckout: (value?: number, currency?: string) => void;

  purchase: (props: PurchaseProps) => void;
}

const loadedGoogleAdsIds = new Set<string>();
const sentTransactions = new Set<string>();

const loadGoogleAdsScript = (): void => {
  if (document.getElementById('google-ads-script')) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'google-ads-script';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js';
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]): void {
      window.dataLayer.push(args);
    };

  window.gtag('js', new Date());
};

const configureGoogleAds = (googleAdsId: string): void => {
  loadGoogleAdsScript();

  if (loadedGoogleAdsIds.has(googleAdsId)) {
    return;
  }

  window.gtag?.('config', googleAdsId);
  loadedGoogleAdsIds.add(googleAdsId);
};

const GoogleAds = (config: GoogleAdsConfig): GoogleAdsService => {
  const { googleAdsId, conversionLabel } = config;

  if (!googleAdsId) {
    return {
      trackPageView: (): void => undefined,
      viewItem: (): void => undefined,
      addToCart: (): void => undefined,
      beginCheckout: (): void => undefined,
      purchase: (): void => undefined,
    };
  }

  configureGoogleAds(googleAdsId);

  return {
    trackPageView: (): void => {
      window.gtag?.('config', googleAdsId, {
        page_path: window.location.pathname + window.location.search,
      });
    },

    viewItem: ({ itemId, itemName, value, currency = 'BRL' }: ViewItemProps): void => {
      window.gtag?.('event', 'view_item', {
        send_to: googleAdsId,
        currency,
        value,
        items: [
          {
            id: itemId,
            name: itemName,
          },
        ],
      });
    },

    addToCart: (
      productId?: string,
      productName?: string,
      productPrice?: number,
      productQuantity?: number,
    ): void => {
      window.gtag?.('event', 'add_to_cart', {
        send_to: googleAdsId,
        currency: 'BRL',
        value: productPrice,
        items: [
          {
            id: productId,
            name: productName,
            quantity: productQuantity,
            price: productPrice,
          },
        ],
      });
    },

    beginCheckout: (value?: number, currency = 'BRL'): void => {
      window.gtag?.('event', 'begin_checkout', {
        send_to: googleAdsId,
        value,
        currency,
      });
    },

    purchase: ({ orderId, totalValue, currency = 'BRL' }: PurchaseProps): void => {
      if (!conversionLabel || !orderId || totalValue == null) {
        return;
      }

      if (sentTransactions.has(orderId)) {
        return;
      }

      window.gtag?.('event', 'conversion', {
        send_to: `${googleAdsId}/${conversionLabel}`,
        value: totalValue,
        currency,
        transaction_id: orderId,
      });

      sentTransactions.add(orderId);
    },
  };
};

export default GoogleAds;
